import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Video,
  Calendar,
  Clock,
  User,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Phone,
  MessageCircle,
  ShieldCheck,
} from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { VideoCall } from "@/components/VideoCall";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";
import { getUserToken } from "@/lib/authStorage";
import recordingConsentContent from "@/legal-rendered/recording_consent";

export default function VideoCallPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inCall, setInCall] = useState(false);

  // ── Pre-call recording consent (per-session, both parties) ───────────────
  // Regulator requirement (Recording & AI Consent §2): every party sees the
  // pre-call notice before the session starts. We record one legal_acceptances
  // row per (user, booking, recording_consent) — distinct from the global
  // consent the user signed at signup, because the regulator wants per-session
  // evidence (in case the user withdrew consent between signup and now).
  const [recordingConsented, setRecordingConsented] = useState(false);
  const [recordingError, setRecordingError] = useState(null);
  const [recordingBusy, setRecordingBusy] = useState(false);

  useEffect(() => {
    fetchBookingDetails();
    // Re-fetch when the URL :bookingId changes. `fetchBookingDetails` is
    // component-local; adding it to deps would require useCallback for
    // no behaviour change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      // Try to get from user's bookings first
      const response = await axios.get(`${API}/bookings/me`);
      let foundBooking = response.data.find(b => b.id === bookingId);
      
      // If not found in user bookings, try expert bookings
      if (!foundBooking) {
        try {
          const expertResponse = await axios.get(`${API}/expert/bookings`);
          if (expertResponse.data?.upcoming) {
            foundBooking = expertResponse.data.upcoming.find(b => b.id === bookingId);
          }
        } catch (e) {
          // Not an expert or no expert bookings, that's ok
        }
      }
      
      if (!foundBooking) {
        setError("Booking not found or you don't have access to this session");
      } else {
        setBooking(foundBooking);
      }
    } catch (err) {
      console.error("Error fetching booking:", err);
      setError("Failed to load booking details");
    } finally {
      setLoading(false);
    }
  };

  const handleJoinCall = () => {
    if (!recordingConsented) {
      toast.error("Please acknowledge the recording & AI consent before joining.");
      return;
    }
    setInCall(true);
  };

  const handleLeaveCall = () => {
    setInCall(false);
    toast.success("You left the video call");
  };

  // ── Record per-session recording consent ────────────────────────────────
  const handleConsentToggle = async (checked) => {
    if (!checked) { setRecordingConsented(false); return; }
    setRecordingBusy(true);
    setRecordingError(null);
    try {
      // sha256 of the content the user just acknowledged — matches
      // legal_audit.policy_versions.text_hash for recording_consent v1.0.0
      const enc = new TextEncoder().encode(recordingConsentContent);
      const buf = await crypto.subtle.digest("SHA-256", enc);
      const hash = Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      const tok = getUserToken();
      if (!tok) throw new Error("Not signed in.");
      const res = await fetch(`${API}/legal/acceptances`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tok}`,
        },
        body: JSON.stringify({
          policy_id: "recording_consent",
          policy_version: "1.1.0",
          text_hash_at_acceptance: hash,
          booking_id: bookingId,
        }),
      });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(detail || `HTTP ${res.status}`);
      }
      setRecordingConsented(true);
    } catch (e) {
      setRecordingError(e.message || "Could not record consent. Please retry.");
      setRecordingConsented(false);
    } finally {
      setRecordingBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex flex-col">
        <MarketingNav />
        <div className="flex-1 flex items-center justify-center pt-16">
          <Loader2 className="w-12 h-12 text-brand-green animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-canvas flex flex-col">
        <MarketingNav />
        <div className="flex-1 flex flex-col items-center justify-center pt-16 px-4">
          <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-ink mb-2 text-center">{error}</h2>
          <Button onClick={() => navigate("/dashboard")} variant="outline" className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      {/* Fixed Header */}
      <MarketingNav />
      
      {/* Main Content - Scrollable area between header and footer */}
      <main className="flex-1 pt-16 sm:pt-20 pb-16 overflow-auto">
        {inCall ? (
          /* Video Call Interface - Contained within app bounds */
          <div className="h-full flex flex-col">
            {/* Session Info Bar */}
            <div className="bg-canvas-card border-b border-slateline-200 px-4 py-3 shrink-0">
              <div className="max-w-6xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-brand-green/10 rounded-full flex items-center justify-center">
                    <Video className="w-4 h-4 sm:w-5 sm:h-5 text-brand-green" />
                  </div>
                  <div>
                    <h1 className="text-sm sm:text-base font-semibold text-ink">Live Session</h1>
                    <p className="text-xs sm:text-sm text-slateline-500">with {booking?.expert_name}</p>
                  </div>
                </div>
                <div className="text-right hidden sm:block">
                  <p className="text-xs text-slateline-500">{booking?.scheduled_date}</p>
                  <p className="text-xs text-brand-green">{booking?.scheduled_time}</p>
                </div>
              </div>
            </div>
            
            {/* Video Call Container - Takes remaining space */}
            <div className="flex-1 p-2 sm:p-4 min-h-0">
              <VideoCall 
                bookingId={bookingId} 
                onLeave={handleLeaveCall}
              />
            </div>
          </div>
        ) : (
          /* Pre-Call Lobby */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto px-4 py-6 sm:py-8"
          >
            {/* Back Button */}
            <Button 
              variant="ghost" 
              onClick={() => navigate("/dashboard")}
              className="mb-4 sm:mb-6 text-slateline-500 hover:text-ink -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>

            <div className="bg-canvas-card border border-slateline-200 rounded-lg p-5 sm:p-8">
              <div className="text-center mb-6 sm:mb-8">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-brand-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Video className="w-8 h-8 sm:w-10 sm:h-10 text-brand-green" />
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-ink mb-2">Ready to Join?</h1>
                <p className="text-sm sm:text-base text-slateline-500">Your video session with {booking?.expert_name}</p>
              </div>

              {/* Session Details */}
              <div className="bg-slateline-100 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
                <h3 className="text-base sm:text-lg font-semibold text-ink mb-4">Session Details</h3>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-brand-green shrink-0" />
                    <div>
                      <p className="text-xs sm:text-sm text-slateline-500">Expert</p>
                      <p className="text-sm sm:text-base text-white">{booking?.expert_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-brand-green shrink-0" />
                    <div>
                      <p className="text-xs sm:text-sm text-slateline-500">Date</p>
                      <p className="text-sm sm:text-base text-white">{booking?.scheduled_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-brand-green shrink-0" />
                    <div>
                      <p className="text-xs sm:text-sm text-slateline-500">Time</p>
                      <p className="text-sm sm:text-base text-white">{booking?.scheduled_time}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4 sm:mb-6">
                <h4 className="text-blue-400 font-medium mb-2 text-sm sm:text-base">Before you join:</h4>
                <ul className="text-slateline-600 text-xs sm:text-sm space-y-1">
                  <li>• Make sure your camera and microphone are working</li>
                  <li>• Find a quiet, well-lit space</li>
                  <li>• Close other applications using your camera</li>
                  <li>• Have a stable internet connection</li>
                </ul>
              </div>

              {/* Recording & AI Consent — required per session */}
              <div className={`border rounded-lg p-4 mb-4 sm:mb-6 ${recordingConsented ? "border-brand-green/30 bg-brand-green/5" : "border-amber-500/30 bg-amber-500/5"}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={recordingConsented}
                    disabled={recordingBusy}
                    onChange={(e) => handleConsentToggle(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-brand-green flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink flex items-center gap-2">
                      <ShieldCheck className={`w-4 h-4 ${recordingConsented ? "text-brand-green" : "text-amber-500"}`} />
                      Recording &amp; AI consent for this session
                    </p>
                    <p className="text-xs text-slateline-600 mt-1 leading-relaxed">
                      I acknowledge this session may be recorded, transcribed, and AI-processed.
                      A red recording indicator will appear in-call. Full details:{" "}
                      <a href="/legal/recording-consent" target="_blank" rel="noreferrer" className="text-brand-green underline">
                        Recording &amp; AI Consent notice
                      </a>.
                    </p>
                    {recordingError && (
                      <p className="text-xs text-red-600 mt-2">{recordingError}</p>
                    )}
                  </div>
                </label>
              </div>

              {/* Join Button — gated on recordingConsented */}
              <Button
                onClick={handleJoinCall}
                disabled={!recordingConsented || recordingBusy}
                className="w-full bg-brand-green hover:bg-brand-greenHover text-white py-5 sm:py-6 text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="join-call-btn"
                title={!recordingConsented ? "Please acknowledge the recording & AI consent first." : ""}
              >
                <Video className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                {recordingConsented ? "Join Video Call" : "Acknowledge consent above to join"}
              </Button>

              {/* Alternative: Open in new tab */}
              {booking?.meeting_link && (
                <Button 
                  onClick={() => window.open(booking.meeting_link, '_blank')}
                  variant="outline"
                  className="w-full mt-3 border-slateline-300 text-slateline-600 hover:text-ink text-sm sm:text-base"
                >
                  Open in New Tab Instead
                </Button>
              )}

              {/* Status Badge */}
              <div className="mt-4 text-center">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm ${
                  booking?.status === "confirmed" 
                    ? "bg-brand-green/15 text-brand-green" 
                    : "bg-yellow-500/20 text-yellow-500"
                }`}>
                  {booking?.status === "confirmed" ? "Session Confirmed" : "Pending Confirmation"}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </main>

      {/* Fixed Footer - Always visible */}
      <footer className="fixed bottom-0 left-0 right-0 bg-canvas-card border-t border-slateline-200 py-2 sm:py-3 px-4 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-brand-green font-display text-sm sm:text-base">
            Xpert
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <a 
              href="tel:+971558945236" 
              className="flex items-center gap-1 text-slateline-500 hover:text-brand-green text-xs sm:text-sm"
            >
              <Phone className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Support</span>
            </a>
            <a 
              href="https://wa.me/971558945236"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-slateline-500 hover:text-brand-green text-xs sm:text-sm"
            >
              <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">WhatsApp</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
